// components/booking/BookingSection.tsx
'use client'

import {useEffect, useState} from 'react';
import {loadStripe} from '@stripe/stripe-js';
import {formatWithOffset} from '@/app/lib/orderUtils';
import {adaptProductDisplayToProduct} from '@/app/lib/productUtil';
import {addMinutes} from 'date-fns';
import {DateTimeSelection} from "@/components/booking/DateTimeSelection";
import {BookingSummary} from "@/components/booking/BookingSummary";
import {AvailableDate, ProductDisplay} from "@/app/api/types";
import {useConfigStore} from '@/app/lib/stores/configStore';

interface BookingSectionProps {
  product: ProductDisplay;
  availableDates: AvailableDate[];
  timezone: string;
  className?: string;
}

export function BookingSection({product, availableDates, timezone, className}: BookingSectionProps) {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);
  const [preparationMessage, setPreparationMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const adaptedProduct = adaptProductDisplayToProduct(product);
  const [stripe, setStripe] = useState<any>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const {stripeKey, initialize, isLoading} = useConfigStore();

  const getFullName = (guide: typeof product.guide) => {
    return `${guide.givenName} ${guide.familyName}`.trim();
  };

  useEffect(() => {
    const initializeConfig = async () => {
      setIsInitializing(true);
      try {
        await initialize();
        setIsInitializing(false);
      } catch (err) {
        console.error('Failed to initialize config:', err);
        setStripeError('Konfiguration konnte nicht geladen werden');
        setIsInitializing(false);
      }
    };

    initializeConfig();
  }, [initialize]);

  useEffect(() => {
    const initializeStripe = async () => {
      if (isInitializing || isLoading || !stripeKey) return;

      try {
        console.log("Initializing Stripe with key:", stripeKey);
        const stripeInstance = await loadStripe(stripeKey);
        if (!stripeInstance) {
          throw new Error('Stripe konnte nicht initialisiert werden');
        }
        setStripe(stripeInstance);
      } catch (err) {
        console.error('Stripe initialization error:', err);
        setStripeError('Stripe konnte nicht initialisiert werden');
      }
    };

    initializeStripe();
  }, [stripeKey, isInitializing, isLoading]);


  const handleBookingConfirmation = async () => {
    if (isInitializing || isLoading) {
      setStripeError('Konfiguration wird noch geladen. Bitte versuche es gleich noch einmal.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setStripeError(null);

    if (!selectedDate || !selectedTimeSlot) {
      console.log("Please select both a date and a time slot.");
      setLoading(false);
      return;
    }

    const dateObject = new Date(selectedDate);
    const [hours, minutes] = selectedTimeSlot.split(':').map(Number);

    if (isNaN(hours) || isNaN(minutes)) {
      console.log("Invalid time format. Please try selecting the time slot again.");
      setLoading(false);
      return;
    }

    dateObject.setHours(hours, minutes, 0, 0);
    const formattedStartDateTime = formatWithOffset(dateObject);
    const endDateTime = addMinutes(dateObject, product.duration);
    const formattedEndDateTime = formatWithOffset(endDateTime);

    const orderData = {
      customerId: "",
      guideId: product.guide.id,
      productId: product.id,
      scheduledStart: formattedStartDateTime,
      scheduledEnd: formattedEndDateTime,
      customerMessage: preparationMessage,
      provisionId: "",
      price: product.price,
    };

    try {
      const response = await fetch('/api/order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });

      if (response.status === 401) {
        const currentUrl = window.location.href;
        const encodedCallbackUrl = encodeURIComponent(currentUrl);
        window.location.href = `/auth/signin?callbackUrl=${encodedCallbackUrl}`;
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        setStripeError(errorData.error);
        throw new Error(errorData.error);
      }
      const result = await response.json();
      console.log('Order creation result:', result);

      const {sessionId} = result;

      if (!sessionId) {
        throw new Error('No sessionId returned from server');
      }

      if (!stripe) {
        throw new Error('Stripe wurde nicht korrekt initialisiert');
      }

      const {error} = await stripe.redirectToCheckout({sessionId});

      if (error) {
        console.error('Stripe redirect error:', error);
        setStripeError(error.message || 'Ein Fehler ist während des Checkouts aufgetreten');
      }

    } catch (error) {
      console.error('Error during booking confirmation:', error);
      setStripeError(
        error instanceof Error
          ? error.message
          : 'Ein unbekannter Fehler ist aufgetreten'
      );

      if (error instanceof Error && error.message.includes('500 Internal Server Error')) {
        setStripeError('Bei der Verarbeitung deiner Bestellung ist ein Problem aufgetreten. Bitte versuche es später erneut oder kontaktiere unseren Support.');
      }
    } finally {
      setLoading(false);
    }
  };


  const MAX_CHARS = 300;
  // Modify the onChange handler to limit input
  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    if (text.length <= MAX_CHARS) {
      setPreparationMessage(text);
    }
  };

  const isBookingButtonDisabled = !selectedDate || !selectedTimeSlot || loading;

  return (
    <div className="w-full">
      {/* Date/Time Selection sauber nebeneinander */}
      <div className="max-w-5xl mx-auto mb-6">
        <DateTimeSelection
          availableDates={availableDates}
          timezone={timezone}
          selectedProduct={adaptedProduct}
          onDateTimeSelected={(date, timeSlot) => {
            setSelectedDate(date);
            setSelectedTimeSlot(timeSlot);
          }}
          variant="horizontal"
        />
      </div>

      {/* Preparation Message */}
      {selectedDate && selectedTimeSlot && (
        <div className="mt-4">
          <label htmlFor="preparationMessage" className="block text-sm font-medium text-gray-700">
            Vorbereitungsnotiz (optional)
          </label>
          <textarea
            id="preparationMessage"
            rows={4}
            maxLength={MAX_CHARS}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            placeholder="Gibt es etwas, das dein Guide vorab wissen sollte?"
            value={preparationMessage}
            onChange={handleMessageChange}
          />
          <div className="mt-1 text-sm text-gray-500 flex justify-end">
            {preparationMessage.length}/{MAX_CHARS} Zeichen
          </div>
        </div>
      )}

      {/* Booking Summary */}
      <div className="mt-4">
        <BookingSummary
          guide={{
            id: product.guide.id,
            picture: product.guide.profileImage || "",
            fullName: getFullName(product.guide),
            given_name: product.guide.givenName
          }}
          product={adaptedProduct}
          selectedDate={selectedDate}
          selectedTimeSlot={selectedTimeSlot}
          timezone={timezone}
          onConfirm={handleBookingConfirmation}
          buttonText="WUNSCHTERMIN JETZT BUCHEN"
          disabled={isBookingButtonDisabled}
        />
      </div>

      {/* Error Display */}
      {stripeError && (
        <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-lg">
          {stripeError}
        </div>
      )}
    </div>
  );
}